import { useCallback, useEffect } from 'react';

/**
 * Запасная высота строки (px), используется если строки ещё не отрендерились.
 * Реальная высота строки gravity-ui Table ~ 50px.
 */
const FALLBACK_ROW_HEIGHT = 50;
const MIN_PAGE_SIZE = 2;
const MAX_PAGE_SIZE = 15;

type UseDynamicPageSizeParams = {
    containerRef: React.RefObject<HTMLDivElement>;
    currentPageSize: number;
    onPageSizeChange: (size: number) => void;
    /** При изменении этого значения хук пересчитывает pageSize (например, когда загрузились видео) */
    videosCount?: number;
};

/**
 * Следит за высотой контейнера и динамически обновляет pageSize.
 * Измеряет реальную высоту строки из DOM, чтобы не зависеть от константы.
 */
export function useDynamicPageSize({
    containerRef,
    currentPageSize,
    onPageSizeChange,
    videosCount,
}: UseDynamicPageSizeParams) {
    const computeAndUpdate = useCallback(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        // Измеряем реальную высоту строки из DOM
        const firstRow = container.querySelector<HTMLElement>('tr.g-table__row');
        const rowHeight = firstRow ? firstRow.offsetHeight : FALLBACK_ROW_HEIGHT;
        const effectiveRowHeight = rowHeight > 0 ? rowHeight : FALLBACK_ROW_HEIGHT;

        const availableHeight = container.clientHeight;
        const rowsCount = Math.floor(availableHeight / effectiveRowHeight);
        const clamped = Math.max(MIN_PAGE_SIZE, Math.min(MAX_PAGE_SIZE, rowsCount));

        if (clamped !== currentPageSize) {
            onPageSizeChange(clamped);
        }
    }, [containerRef, currentPageSize, onPageSizeChange]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        computeAndUpdate();

        const observer = new ResizeObserver(() => {
            computeAndUpdate();
        });
        observer.observe(container);

        return () => observer.disconnect();
    }, [computeAndUpdate]);

    useEffect(() => {
        const raf = requestAnimationFrame(() => {
            computeAndUpdate();
        });
        return () => cancelAnimationFrame(raf);
    }, [videosCount]);
}
